import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as NumberFormatHelper from '../../../helpers/NumberFormatHelper';
import OrderService from '../../../services/OrderService';
import { Grid, LinearProgress, Box } from '@mui/material';
import { MAX_API_CALLS_FOR_POLLING, TIME_OUT_MESSAGE, QR_PREFIX, STORAGE_KEY } from '../../../const';
import styles from './style.module.css';
import QRCode from 'qrcode.react';
import { OrderContext } from '../../../contexts/OrderContext';
import { ShopContext } from '../../../contexts/ShopContext';
import { CartContext } from '../../../contexts/CartContext';
import { PaymentContext } from '../../../contexts/PaymentContext';
import { handlePayment } from '../../utils/HandlePayment';
import PageHeader from '../../utils/PageHeader/PageHeader';
import SuccessIcon from '../../icons/success_icon.png';
import ProductView from '../../utils/ProductView/ProductView';
import { PAYMENT_TYPE } from '../../../const';
import CustomButton from '../../utils/CustomerComponents/CustomButton';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';
import CacheHelper from '../../../helpers/CacheHelper';
import { PaymentHelper } from '../../../helpers/PaymentHelper';
import NavBar from '../../utils/NavBar/NavBar';

function PaymentStatus() {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const paymentId = queryParams.get('payment_id');
  const secret = queryParams.get('secret');
  const paymentType = queryParams.get('payment_type');
  const orderId = queryParams.get('order_id');

  const { countOrders } = useContext(OrderContext);
  const { shop } = useContext(ShopContext);
  const { cart, clearCartItems } = useContext(CartContext);
  const { handleRemovePayment } = useContext(PaymentContext)

  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const getOrderDataFromCache = () => {
    const cart = localStorage.getItem(`${shop.shopId}-${STORAGE_KEY.CART}`);
    const cartData = JSON.parse(cart);

    const orderDataFallback = {
      details: {
        items: Object.values(cartData.products).map(({ product, quantity }) => ({
          id: product.id,
          name: product.name,
          rate: product.price,
          image: product.images[0],
          quantity: quantity,
        })),
        amount: cartData?.summary.total,
        subTotal: cartData?.summary.subTotal,
        tax: cartData?.summary.tax,
        discount: cartData?.summary.discount || cartData?.summary.totalSavings,
        payable: cartData?.summary.payable,
      },
    };
    setOrderData(orderDataFallback);
  };

  useEffect(() => {
    let isCancelled = false;

    const pollPaymentStatus = async (attempt = 0) => {
      if (attempt >= MAX_API_CALLS_FOR_POLLING || isCancelled || orderData || showLoading || isRetrying) {
        if (attempt >= MAX_API_CALLS_FOR_POLLING && !orderData) {
          setError(TIME_OUT_MESSAGE);
          getOrderDataFromCache();
        }
        return;
      }

      const shopId = shop?.shopId || localStorage.getItem(STORAGE_KEY.SHOP_ID);
      let response = null;
      try {
        response = await OrderService.getOrderByPayment(shopId, paymentId, {
          payment_secret: secret,
        });
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          setError(err.message || 'Error while checking payment status');
        }
      }

      if (isCancelled) {
        return;
      }

      if (!response) {
        setTimeout(() => pollPaymentStatus(attempt + 1), 4000);
        return;
      }

      setOrderData(response);
      CacheHelper.saveOrderToLocalStorage(response, shopId);
      PaymentHelper.removePayment(shopId, paymentId);
      handleRemovePayment(response?.online_payment?.id)
      countOrders();
      clearCartItems(shopId);
    };

    const queryError = queryParams.get('error');
    if (error || queryError) {
      if (queryError) setError(queryError);
      getOrderDataFromCache();
      return;
    }

    if (!error && !(orderId && paymentType === PAYMENT_TYPE.NO_PAYMENT)) {
      pollPaymentStatus(0);
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log(error);

  const retryPayment = async () => {
    setShowLoading(true);
    setIsRetrying(true);
    setError(null);
    try {
      await handlePayment({
        shop,
        cart,
        customerDetails: null,
        setShowLoading,
        setError,
        history,
      });
    } catch (err) {
      setError(err.message || 'An error occurred during payment retry.');
    }
  };

  return (
    <div className={styles.container}>
      {showLoading ? (
        <Grid className={styles.loderContaner} flexDirection="column">
          <p style={{ paddingBottom: 20 }}>Please wait....</p>
          <Box sx={{ width: '40%' }}>
            <LinearProgress />
          </Box>
        </Grid>
      ) : !orderData && !error ? (
        <Grid className={styles.loderContaner} flexDirection="column">
          <p style={{ paddingBottom: 20 }}>Verifying Payment .....</p>
          <Box sx={{ width: '40%' }}>
            <LinearProgress />
          </Box>
          <NavBar />
        </Grid>
      ) : (
        <>
          <div className={styles.statusContainer}>
            <div
              className={styles.statusContent}
              style={{
                backgroundColor: error ? '#F82222B5' : '#638D54CF',
              }}
            >
              <PageHeader
                title={error ? 'Order Failed' : 'Order Placed'}
                backToHome={true}
                color="#fff"
                showBorder={false}
              />
              <div className={styles.statusWrapper}>
                <img src={SuccessIcon} alt="" />
                <span className={styles.head}>{error ? 'Your Order Failed' : 'Order Placed'}</span>
                <span className={styles.text}>{error ? 'Payment error detected' : 'Thank you for ordering'}</span>
                <span className={styles.text}>{error ? 'Please try again' : 'We are processing your order'}</span>
              </div>
            </div>
          </div>
          <Grid container className={styles.orderContainer}>
            {!error && (
              <Grid item contaner xs={12}>
                <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pt={1.5}>
                  <span className={styles.text2}>Order ID</span>
                  <span className={styles.hilightText}>{orderData?.id}</span>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pt={1.5}>
                  <span className={styles.text2}>Date</span>
                  <span className={styles.hilightText}>
                    {DateTimeHelpers.getDate(error ? undefined : orderData.created_at)}
                  </span>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pt={1.5}>
                  <span className={styles.text2}>Time</span>
                  <span className={styles.hilightText}>
                    {DateTimeHelpers.getTime12HFormat(error ? undefined : orderData.created_at)}
                  </span>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} pt={2}>
              <div className={styles.divider}></div>
            </Grid>
            <Grid item container rowSpacing={1} xs={12} pt={2} display="flex" alignContent="flex-start">
              {(orderData?.details?.items || []).map((item) => (
                <ProductView product={item} orderView={true} showActionSection={false} />
              ))}
            </Grid>
            <Grid item xs={12} pt={2}>
              <div className={styles.divider}></div>
            </Grid>
            <Grid item xs={12} pt={2}>
              <span className={styles.header}>Payment Summary</span>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pt={2}>
              <span className={styles.text2}>Price</span>
              <span className={styles.text3}>
                {NumberFormatHelper.amountFormat(orderData?.details?.subTotal || orderData?.details?.amount || 0, shop)}
              </span>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pt={1}>
              <span className={styles.text2}>Tax</span>
              <span className={styles.text3}>
                {NumberFormatHelper.amountFormat(orderData?.details?.tax || 0, shop)}
              </span>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" pb={1}>
              <span className={styles.text2}>Discount</span>
              <span className={styles.text3}>
                {NumberFormatHelper.amountFormat(orderData?.details?.discount || 0, shop)}
              </span>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt={1}
              className={styles.divTopBorder}
            >
              <span className={styles.text2}>Total Payment</span>
              <span className={styles.text3}>
                {NumberFormatHelper.amountFormat(orderData?.details?.payable || orderData?.details?.amount || 0, shop)}
              </span>
            </Grid>
            {!error && (
              <>
                {' '}
                <Grid item xs={12} pt={2}>
                  <div className={styles.divider}></div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  pt={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                >
                  <span className={styles.text2} style={{ lineHeight: '18px' }}>
                    Scan your QR code at the counter and <br />
                    grab your order hassle-free!
                  </span>
                </Grid>
                <Grid item xs={12} pt={2.5} display="flex" alignItems="center" justifyContent="center" pb={5}>
                  <QRCode
                    value={QR_PREFIX + JSON.stringify({ id: orderData?.id, secret: orderData?.secret })}
                    size={140}
                    style={{ borderRadius: '12px' }}
                  />
                </Grid>
              </>
            )}
            {error && (
              <>
                <Grid item xs={12} className={styles.btnContainer}>
                  <CustomButton fullWidth onClick={retryPayment}>
                    Try again
                  </CustomButton>
                </Grid>
                <Grid item xs={12} style={{ height: '90px' }}></Grid>
              </>
            )}
          </Grid>
        </>
      )}
    </div>
  );
}

export default PaymentStatus;
